import { useCallback, useEffect } from "react";
import { TriggeredHook } from "sonobello.utilities.react";
import { UseApiErrorResponse } from "sonobello.utilities.react.axios";

import { ObxError } from "../../../../dtos/ObxError";
import { UseObxCustom, useObx } from "../../../../utils/UseObx";
import { ErrorReport } from "../../../App/Types/ErrorReport";
import ApiRequestPaths from "../../../Constants/ApiRequestPaths";

export interface IUseGetCalendarEventRequest {
  /** The id of the contact associated with the appointment. */
  contactId: string;
  /** The id of the patient associated with the appointment. */
  patientId: string;
  /** The id of the appointment to obtain the calendar event url for. */
  appointmentId: string;
}

const calendarEventError = (
  useObxError: UseApiErrorResponse<undefined, UseObxCustom<Record<string, unknown>>, ObxError>
) =>
  `Failed to obtain the calendar event url with status code '${useObxError.error.code}': ${useObxError.error.message}`;

/** A hook which obtains the calendar event url for the given request params.
 * Reports failure to the error report endpoint.
 * @remarks The hook value is `null` until the request is complete, after which the value is either the url or
 * `undefined` if an error occurred.
 */
const useGetCalendarEventUrl: TriggeredHook<string, IUseGetCalendarEventRequest> = () => {
  const {
    res: calendarResult,
    err: calendarError,
    loading: calendarLoading,
    setReq: setCalendarEventReq
  } = useObx<string>("GET CalendarEvent");
  const { loading: errorReportLoading, setReq: setErrorReportReq } = useObx<void, ErrorReport>("POST ErrorReport", {
    method: "post",
    url: ApiRequestPaths.errorReport
  });

  const execute = useCallback(
    ({ contactId, patientId, appointmentId }: IUseGetCalendarEventRequest) =>
      setCalendarEventReq({
        url: ApiRequestPaths.getCalendarEventUrl(contactId, patientId, appointmentId),
        custom: {}
      }),
    []
  );

  useEffect(() => {
    if (calendarError)
      setErrorReportReq(
        r => r && { ...r, payload: new ErrorReport(new Error(calendarEventError(calendarError)), "no stack") }
      );
  }, [calendarError]);

  return {
    result: (!errorReportLoading && calendarResult?.payload) || null,
    execute,
    isLoading: calendarLoading || errorReportLoading,
    error: !errorReportLoading && Boolean(calendarError)
  };
};

export default useGetCalendarEventUrl;
